import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

const CommandsContainer = styled.div`
  max-width: 1000px;
  margin: 60px auto;
  padding: 0 20px;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 20px;
  text-align: center;
  
  span {
    color: ${theme.colors.primary};
  }
`;

const PageDescription = styled.p`
  text-align: center;
  color: ${theme.colors.textSecondary};
  margin-bottom: 50px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const CommandsSection = styled.section`
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 10px;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const CommandCard = styled.div`
  background-color: ${theme.colors.backgroundAlt};
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${theme.colors.primary};
`;

const CommandName = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  
  code {
    background-color: ${theme.colors.background};
    padding: 5px 10px;
    border-radius: 4px;
    font-family: monospace;
    margin-right: 10px;
  }
  
  span {
    color: ${theme.colors.textSecondary};
    font-size: 1rem;
    margin-left: 10px;
  }
`;

const CommandDescription = styled.p`
  color: ${theme.colors.textSecondary};
  margin-bottom: 15px;
`;

const CommandUsage = styled.div`
  background-color: ${theme.colors.background};
  border-radius: 4px;
  padding: 15px;
  margin-top: 15px;
  
  h4 {
    font-size: 1rem;
    margin-bottom: 10px;
    color: ${theme.colors.primary};
  }
  
  code {
    font-family: monospace;
    display: block;
    margin-bottom: 10px;
  }
  
  p {
    font-size: 0.9rem;
    color: ${theme.colors.textSecondary};
  }
`;

const CommandParameters = styled.div`
  margin-top: 15px;
  
  h4 {
    font-size: 1rem;
    margin-bottom: 10px;
  }
  
  ul {
    list-style-type: none;
    
    li {
      margin-bottom: 8px;
      color: ${theme.colors.textSecondary};
      
      strong {
        color: ${theme.colors.secondary};
        margin-right: 5px;
      }
      
      em {
        color: ${theme.colors.accent};
        font-style: normal;
      }
    }
  }
`;

const Commands = () => {
  return (
    <CommandsContainer>
      <PageTitle>Comandos <span>Disponibles</span></PageTitle>
      <PageDescription>
        Aquí encontrarás todos los comandos disponibles para el bot TuSecreto, 
        con ejemplos de uso y descripciones detalladas.
      </PageDescription>
      
      <CommandsSection>
        <SectionTitle>Comandos para Usuarios</SectionTitle>
        
        <CommandCard>
          <CommandName>
            <code>/secreto</code>
          </CommandName>
          <CommandDescription>
            Envía un secreto anónimo al canal configurado. Nadie sabrá que fuiste tú quien lo envió.
          </CommandDescription>
          <CommandParameters>
            <h4>Parámetros:</h4>
            <ul>
              <li><strong>contenido</strong> - El texto del secreto que quieres compartir.</li>
              <li><strong>nsfw</strong> <em>(opcional)</em> - Marca el secreto como contenido para adultos.</li>
            </ul>
          </CommandParameters>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/secreto contenido: Me gusta la pizza con piña nsfw: No</code>
            <p>El secreto aparecerá en el canal configurado sin mostrar quién lo envió.</p>
          </CommandUsage>
        </CommandCard>
        
        <CommandCard>
          <CommandName>
            <code>/nivel</code>
          </CommandName>
          <CommandDescription>
            Muestra tu nivel actual basado en la cantidad de secretos que has compartido.
          </CommandDescription>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/nivel</code>
            <p>Muestra información sobre tu nivel, cantidad de secretos compartidos y progreso.</p>
          </CommandUsage>
        </CommandCard>
        
        <CommandCard>
          <CommandName>
            <code>/ranking</code>
          </CommandName>
          <CommandDescription>
            Muestra un ranking de los secretos más populares según las reacciones recibidas.
          </CommandDescription>
          <CommandParameters>
            <h4>Parámetros:</h4>
            <ul>
              <li><strong>tipo</strong> <em>(opcional)</em> - Tipo de ranking: "local" (solo este servidor) o "global" (todos los servidores).</li>
            </ul>
          </CommandParameters>
          <CommandUsage>
            <h4>Ejemplos:</h4>
            <code>/ranking tipo: local</code>
            <p>Muestra los secretos más populares de tu servidor.</p>
            <code>/ranking tipo: global</code>
            <p>Muestra los secretos globales más populares de todos los servidores.</p>
          </CommandUsage>
        </CommandCard>
        
        <CommandCard>
          <CommandName>
            <code>/ayuda</code>
          </CommandName>
          <CommandDescription>
            Muestra la lista de comandos disponibles y su descripción.
          </CommandDescription>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/ayuda</code>
            <p>Muestra un mensaje con todos los comandos disponibles y su descripción.</p>
          </CommandUsage>
        </CommandCard>
      </CommandsSection>
      
      <CommandsSection>
        <SectionTitle>Comandos para Administradores</SectionTitle>
        
        <CommandCard>
          <CommandName>
            <code>/config-secretos canal</code>
          </CommandName>
          <CommandDescription>
            Establece el canal donde se enviarán los secretos.
          </CommandDescription>
          <CommandParameters>
            <h4>Parámetros:</h4>
            <ul>
              <li><strong>canal</strong> - El canal donde se enviarán los secretos.</li>
            </ul>
          </CommandParameters>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/config-secretos canal: #secretos</code>
            <p>Configura el canal #secretos como el canal donde se enviarán los secretos.</p>
          </CommandUsage>
        </CommandCard>
        
        <CommandCard>
          <CommandName>
            <code>/config-secretos activar</code>
          </CommandName>
          <CommandDescription>
            Activa el sistema de secretos en el servidor.
          </CommandDescription>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/config-secretos activar</code>
            <p>Activa el sistema de secretos para que los usuarios puedan enviar secretos.</p>
          </CommandUsage>
        </CommandCard>
        
        <CommandCard>
          <CommandName>
            <code>/config-secretos desactivar</code>
          </CommandName>
          <CommandDescription>
            Desactiva el sistema de secretos en el servidor.
          </CommandDescription>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/config-secretos desactivar</code>
            <p>Desactiva el sistema de secretos temporalmente.</p>
          </CommandUsage>
        </CommandCard>
        
        <CommandCard>
          <CommandName>
            <code>/config-secretos global-activar</code>
          </CommandName>
          <CommandDescription>
            Activa los secretos globales en el servidor.
          </CommandDescription>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/config-secretos global-activar</code>
            <p>Permite que los secretos enviados en este servidor sean visibles en todos los servidores que tienen activados los secretos globales.</p>
          </CommandUsage>
        </CommandCard>
        
        <CommandCard>
          <CommandName>
            <code>/config-secretos global-desactivar</code>
          </CommandName>
          <CommandDescription>
            Desactiva los secretos globales en el servidor.
          </CommandDescription>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/config-secretos global-desactivar</code>
            <p>Los secretos enviados en este servidor solo serán visibles localmente.</p>
          </CommandUsage>
        </CommandCard>
        
        <CommandCard>
          <CommandName>
            <code>/config-secretos estado</code>
          </CommandName>
          <CommandDescription>
            Muestra el estado actual de la configuración del sistema de secretos.
          </CommandDescription>
          <CommandUsage>
            <h4>Ejemplo:</h4>
            <code>/config-secretos estado</code>
            <p>Muestra información sobre la configuración actual del sistema de secretos.</p>
          </CommandUsage>
        </CommandCard>
      </CommandsSection>
    </CommandsContainer>
  );
};

export default Commands; 